import React from 'react';
import {
  handleCreditCardExpirationMaskOnInput,
  handleCreditCardNumberMaskOnInput,
  handleCpfMaskOnInput,
} from '../../../../utils/payment/masks';
import type { PaymentFormErrors } from '../types/payment-form.types';
import { type CreditCardData } from '../../../../types';
import type { InstallmentOption } from '../../../../offer.types';
import { useLatam3DS } from '../../../../services/card3ds';
import StripeCardElementsForm from './StripeCardElementsForm';
import PagouPaymentElementForm from './PagouPaymentElementForm';

interface CreditCardFormProps {
  creditCardData: CreditCardData;
  errors: PaymentFormErrors;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  installmentOptions?: InstallmentOption[];
  on3DSProcessing?: (isProcessing: boolean) => void;
  on3DSError?: (error: string) => void;
  onSubmit?: (formData: CreditCardData) => Promise<void>;
  /** Quando Stripe é o gateway e tem publishableKey, usa CardElements (PCI compliant) */
  creditCardPrimaryGateway?: string;
  stripePublishableKey?: string;
  pagouPublicKey?: string;
  pagouProduction?: boolean;
  /** Public Key Pagar.me — não altera UI (tokenização no place-order). */
  pagarmePublicKey?: string;
  /** Carrega hook 3DS apenas quando true (ex: Latam) */
  shouldLoad3DS?: boolean;
  onCardElementReady?: (handlers: { getCardElement: () => any; getStripe: () => unknown }) => void;
  /** Recebe o token reutilizável `pgpm_` (Pagou) quando há upsell one-click. */
  onReusableToken?: (token: string) => void;
  /** Quando true, o Payment Element da Pagou gera token reutilizável (`mode: "upsell"`). */
  hasOneClickUpsell?: boolean;
}

const CreditCardForm: React.FC<CreditCardFormProps> = ({
  creditCardData,
  errors,
  onChange,
  installmentOptions,
  on3DSProcessing,
  on3DSError,
  creditCardPrimaryGateway,
  stripePublishableKey,
  pagouPublicKey,
  pagouProduction,
  shouldLoad3DS = false,
  onCardElementReady,
  onReusableToken,
  hasOneClickUpsell,
}) => {
  const useStripeElements = creditCardPrimaryGateway === 'stripe' && !!stripePublishableKey;
  const usePagouElements = creditCardPrimaryGateway === 'pagou' && !!pagouPublicKey;

  const inputBaseClass = 'w-full p-3 border rounded-md shadow-sm text-sm focus:ring-primary focus:border-primary bg-input-bg-light dark:bg-input-bg-dark text-text-primary dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500';
  const errorBorderClass = 'border-red-500';
  const normalBorderClass = 'border-medium-gray dark:border-gray-600';

  // Hook 3DS Latam - carregado apenas quando o gateway declara que precisa
  const { 
    isLoading: is3DSLoading, 
    error: threeDSError
  } = useLatam3DS(shouldLoad3DS);

  // Notifica mudanças no processamento 3DS
  React.useEffect(() => {
    if (on3DSProcessing) {
      on3DSProcessing(is3DSLoading);
    }
  }, [is3DSLoading, on3DSProcessing]);

  // Notifica erros 3DS
  React.useEffect(() => {
    if (threeDSError && on3DSError) {
      on3DSError(threeDSError);
    }
  }, [threeDSError, on3DSError]);




  if (useStripeElements) {
    return (
      <StripeCardElementsForm
        creditCardData={creditCardData}
        errors={errors}
        onChange={onChange}
        stripePublishableKey={stripePublishableKey!}
        onCardElementReady={onCardElementReady}
      />
    );
  }

  return (
    <div className="space-y-4">
      {usePagouElements ? (
        <PagouPaymentElementForm
          errors={errors}
          pagouPublicKey={pagouPublicKey!}
          pagouProduction={pagouProduction}
          hasOneClickUpsell={hasOneClickUpsell}
          onReusableToken={onReusableToken}
        />
      ) : (
        <>
          {/* Número do cartão - linha completa */}
          <div>
            <label htmlFor="cardNumber" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
              Número do cartão
            </label>
            <input
              type="text"
              name="cardNumber"
              id="cardNumber"
              value={creditCardData.cardNumber}
              onChange={onChange}
              placeholder="0000 0000 0000 0000"
              inputMode="numeric"
              maxLength={19}
              minLength={16}
              onInput={handleCreditCardNumberMaskOnInput}
              className={`${inputBaseClass} ${errors.cardNumber ? errorBorderClass : normalBorderClass}`}
            />
            {errors.cardNumber && <p className="text-xs text-red-500 mt-1">{errors.cardNumber}</p>}
          </div>

          {/* Validade e Código de segurança - lado a lado */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="expirationDate" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
                Validade (mês/ano)
              </label>
              <input
                type="text"
                name="expirationDate"
                id="expirationDate"
                value={creditCardData.expirationDate}
                onChange={onChange}
                placeholder="MM/AA"
                inputMode="numeric"
                maxLength={5}
                minLength={5}
                onInput={handleCreditCardExpirationMaskOnInput}
                className={`${inputBaseClass} ${errors.expirationDate ? errorBorderClass : normalBorderClass}`}
              />
              {errors.expirationDate && <p className="text-xs text-red-500 mt-1">{errors.expirationDate}</p>}
            </div>
            <div>
              <label htmlFor="cvv" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
                Cód. de segurança
              </label>
              <input
                type="text"
                name="cvv"
                id="cvv"
                value={creditCardData.cvv}
                onChange={onChange}
                placeholder="123"
                inputMode="numeric"
                maxLength={4}
                minLength={3}
                className={`${inputBaseClass} ${errors.cvv ? errorBorderClass : normalBorderClass}`}
              />
              {errors.cvv && <p className="text-xs text-red-500 mt-1">{errors.cvv}</p>}
            </div>
          </div>
        </>
      )}

      {/* No Pagou, o nome do titular já é coletado dentro do iframe */}
      {!usePagouElements && (
        <div>
          <label htmlFor="cardholderName" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
            Nome e sobrenome do titular
          </label>
          <input
            type="text"
            name="cardholderName"
            id="cardholderName"
            value={creditCardData.cardholderName}
            onChange={onChange}
            placeholder="Nome como está no cartão"
            maxLength={60}
            minLength={1}
            className={`${inputBaseClass} ${errors.cardholderName ? errorBorderClass : normalBorderClass}`}
          />
          {errors.cardholderName && <p className="text-xs text-red-500 mt-1">{errors.cardholderName}</p>}
        </div>
      )}

      {/* CPF do titular - último campo do formulário de cartão */}
      <div>
        <label htmlFor="cpf" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
          CPF do titular
        </label>
        <input
          type="text"
          name="cpf"
          id="cpf"
          value={creditCardData.cpf ?? ''}
          onChange={onChange}
          onInput={handleCpfMaskOnInput}
          placeholder="000.000.000-00"
          inputMode="numeric"
          maxLength={14}
          className={`${inputBaseClass} ${errors.cpf ? errorBorderClass : normalBorderClass}`}
        />
        {errors.cpf && <p className="text-xs text-red-500 mt-1">{errors.cpf}</p>}
      </div>

      {/* Número de parcelas - força altura real no iOS/WebKit */}
      <div>
        <label htmlFor="installments" className="block text-sm font-medium text-text-secondary dark:text-gray-400 mb-1">
          Nº de Parcelas
        </label>
        <div className="relative">
          <select
            name="installments"
            id="installments"
            value={creditCardData.installments}
            onChange={onChange}
            style={{
              height: '48px',
              padding: '12px 40px 12px 12px',
              WebkitAppearance: 'none',
              appearance: 'none',
              lineHeight: '24px',
            }}
            className={`${inputBaseClass} ${errors.installments ? errorBorderClass : normalBorderClass}`}
          >
            <option value="">Selecione...</option>
            {installmentOptions && installmentOptions.length > 0 ? (
              installmentOptions.map(opt => (
                <option key={opt.installments} value={opt.installments}>
                  {opt.installments}x de R$ {opt.amount.toFixed(2).replace('.', ',')}
                  {opt.has_interest ? '' : ' sem juros'}
                  {opt.has_interest && opt.interest_rate > 0 ? ` *` : ''}
                </option>
              ))
            ) : (
              // Fallback para opções básicas se não houver do backend
              <>
                <option value="1">1x de R$ {(parseFloat(creditCardData.installments) || 1) === 1 ? '399,00' : '399,00'}</option>
                <option value="12">12x de R$ 40,06 *</option>
              </>
            )}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-text-secondary dark:text-gray-400">
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        {errors.installments && <p className="text-xs text-red-500 mt-1">{errors.installments}</p>}
      </div>

      {/* Indicador de processamento 3DS */}
      {is3DSLoading && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-sm text-blue-700 dark:text-blue-300">
              Processando autenticação 3D Secure...
            </span>
          </div>
        </div>
      )}

      {/* Indicador de erro 3DS */}
      {threeDSError && (
        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <span className="text-sm text-red-700 dark:text-red-300">
            Erro na autenticação 3D Secure: {threeDSError}
          </span>
        </div>
      )}
    </div>
  );
};

export default CreditCardForm; 